import { motion } from 'framer-motion';
import GlowBackground from '@/components/site/GlowBackground';
import SectionHeader from '@/components/site/SectionHeader';
import { ShieldCheck, Landmark, Users, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const values = [
  { icon: ShieldCheck, title: 'Confidential', body: 'Your enquiry is treated with discretion and handled in line with POPIA.' },
  { icon: Landmark, title: 'Professional', body: 'Enquiries reviewed by South African attorneys with civil claims experience.' },
  { icon: Users, title: 'Responsive', body: 'We aim to reply within business hours — usually via WhatsApp.' },
  { icon: MapPin, title: 'South African', body: 'Assisting clients across South Africa — enquiries welcome from all nine provinces.' },
];

export default function About() {
  return (
    <div className="relative pt-28 md:pt-36 pb-16" data-testid="about-page">
      <div className="absolute inset-0 pointer-events-none"><GlowBackground /></div>
      <div className="relative mx-auto max-w-6xl px-5 md:px-8">
        <SectionHeader
          eyebrow="About us"
          title={<>Access to justice, <span className="text-[#FFD400]">made simple.</span></>}
          subtitle="Wrongfully Arrested SA helps South Africans understand whether their arrest or detention may have been unlawful — and connects them with legal professionals who can investigate."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-white/10 bg-[#0d1826]/60 p-8 md:p-10"
          >
            <div className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">01 — Mission</div>
            <h3 className="mt-4 font-poppins font-semibold text-white text-2xl md:text-3xl leading-tight">Bridge the gap between the arrested and the law.</h3>
            <p className="mt-5 font-inter text-white/70 leading-relaxed">
              We built this platform because most South Africans do not know they may have a civil claim after an unlawful arrest. Our online assessment removes the guesswork.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="rounded-3xl border border-white/10 bg-[#0d1826]/60 p-8 md:p-10"
          >
            <div className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">02 — How we help</div>
            <h3 className="mt-4 font-poppins font-semibold text-white text-2xl md:text-3xl leading-tight">Assess. Explain. Connect.</h3>
            <p className="mt-5 font-inter text-white/70 leading-relaxed">
              Complete the online calculator. If your matter has grounds to investigate, we connect you with legal professionals experienced in civil claims against the Minister of Police.
            </p>
          </motion.div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              data-testid={`about-value-${i}`}
              className="rounded-2xl border border-white/[0.06] bg-[#0d1826]/50 p-6"
            >
              <v.icon className="h-6 w-6 text-[#39A8FF]" strokeWidth={1.5} />
              <div className="mt-6 font-poppins font-semibold text-white text-lg">{v.title}</div>
              <p className="mt-2 font-inter text-white/60 text-sm leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link to="/calculator" data-testid="about-cta" className="inline-flex items-center gap-2 rounded-full bg-[#FFD400] px-8 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F]">
            Start Assessment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
